/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Creates a throttled function that only invokes the provided function at most once
 * every X milliseconds, regardless of how many times the throttled function is called.
 * The last call will always be executed after the throttling period.
 *
 * @param callback - The function to throttle.
 * @param interval - The number of milliseconds to wait between allowed function calls. Defaults to 200ms.
 * @returns A new throttled function.
 * @example const throttled = throttle(() => console.log("Hello, World!"), 1000);
 */
export const throttle = <T extends (...args: any[]) => any>(
  callback: T,
  interval: number = 200,
): ((...args: Parameters<T>) => void) => {
  let isThrottled = false;
  let timeoutID: ReturnType<typeof setTimeout> | null = null;
  let savedArgs: Parameters<T> | null = null;
  let savedThis: unknown = null;

  return function (this: unknown, ...args: Parameters<T>) {
    if (isThrottled) {
      // Save latest call context and arguments
      savedArgs = args;
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      savedThis = this;
      return;
    }

    // Execute the first call immediately
    callback.apply(this, args);
    isThrottled = true;

    // Clear any existing timeout
    if (timeoutID) {
      clearTimeout(timeoutID);
    }

    // Schedule the next possible execution
    timeoutID = setTimeout(() => {
      isThrottled = false;

      if (savedArgs) {
        // If we have saved arguments, execute them and clear the saved state
        callback.apply(savedThis, savedArgs);
        savedArgs = null;
        savedThis = null;

        // Schedule the next execution window
        timeoutID = setTimeout(() => {
          isThrottled = false;
        }, interval);
      }
    }, interval);
  };
};
